
import { Link } from 'react-router-dom';

interface Deal {
  id: string;
  name: string;
  category: string;
  image: string;
  description: string;
  rating: number;
  reviews: number;
  clicks: number;
  affiliateLink: string;
}

interface RelatedDealsProps {
  deals: Deal[];
}

export default function RelatedDeals({ deals }: RelatedDealsProps) {
  if (!deals || deals.length === 0) return null;

  return (
    <section className="px-6 lg:px-8 pb-24">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-xl font-bold text-white mb-8 flex items-center gap-3">
          <div className="w-8 h-8 bg-neon/10 flex items-center justify-center rounded-none">
            <i className="ri-apps-line text-neon text-lg"></i>
          </div>
          Similar Deals
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-product-shop>
          {deals.map((deal) => {
            const badge =
              deal.clicks > 3500
                ? {
                    text: 'Most Popular',
                    icon: 'ri-fire-fill',
                    cls: 'bg-neon/10 text-neon',
                  }
                : deal.clicks > 2500
                ? {
                    text: 'Trending',
                    icon: 'ri-thumb-up-fill',
                    cls: 'bg-amber-500/10 text-amber-400',
                  }
                : {
                    text: 'Recommended',
                    icon: 'ri-star-line',
                    cls: 'bg-white/5 text-white/50',
                  };

            return (
              <Link
                key={deal.id}
                to={`/websites/${deal.id}`}
                className="bg-white/5 border border-white/5 rounded-none overflow-hidden hover:border-neon/20 transition-all group cursor-pointer block"
              >
                <div className="relative w-full h-48 bg-white/5 overflow-hidden">
                  <img
                    src={deal.image}
                    alt={deal.name}
                    title={`${deal.name} affiliate deal`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 right-3 px-2.5 py-1 bg-black/60 backdrop-blur-sm text-white/80 text-xs font-medium rounded-none">
                    {deal.category}
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-white mb-1.5 group-hover:text-neon transition-colors">
                    {deal.name}
                  </h3>
                  <p className="text-white/40 text-sm mb-4 leading-relaxed line-clamp-2">
                    {deal.description}
                  </p>
                  <div className="flex items-center gap-2 pt-4 border-t border-white/5">
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-none text-xs font-semibold ${badge.cls}`}
                    >
                      <i className={`${badge.icon} text-xs`}></i>
                      {badge.text}
                    </span>
                    <div className="flex items-center gap-1 ml-auto">
                      <i className="ri-star-fill text-amber-400 text-xs"></i>
                      <span className="text-white/60 text-xs font-medium">{deal.rating}</span>
                      <span className="text-white/30 text-xs">
                        ({deal.reviews?.toLocaleString()})
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
